import { useEffect } from 'react';

interface SEOHeadProps {
  title: string;
  description: string;
  keywords?: string;
  canonicalUrl?: string;
  ogType?: string;
  schema?: Record<string, unknown>;
}

export default function SEOHead({
  title,
  description,
  keywords,
  canonicalUrl,
  ogType = 'website',
  schema,
}: SEOHeadProps) {
  useEffect(() => {
    document.title = title;

    const setMeta = (name: string, content: string, propName = 'name') => {
      let el = document.querySelector(`meta[${propName}="${name}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(propName, name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    setMeta('description', description);
    if (keywords) setMeta('keywords', keywords);
    if (canonicalUrl) {
      let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
      if (!link) {
        link = document.createElement('link');
        link.rel = 'canonical';
        document.head.appendChild(link);
      }
      link.href = canonicalUrl;
    }

    setMeta('og:title', title, 'property');
    setMeta('og:description', description, 'property');
    if (canonicalUrl) setMeta('og:url', canonicalUrl, 'property');
    setMeta('og:type', ogType, 'property');

    setMeta('twitter:title', title);
    setMeta('twitter:description', description);

    if (schema) {
      const existingScript = document.getElementById('page-jsonld');
      if (existingScript) existingScript.remove();
      const script = document.createElement('script');
      script.id = 'page-jsonld';
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify(schema);
      document.head.appendChild(script);
    }

    return () => {
      const pageScript = document.getElementById('page-jsonld');
      if (pageScript) pageScript.remove();
    };
  }, [title, description, keywords, canonicalUrl, ogType, schema]);

  return null;
}